import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import StatsCard from '../../components/common/StatsCard';
import { DrawerForm } from '../../components/ui/drawer-form';
import CreateLeadForm from '../../components/leads/CreateLeadForm';
import { getStatusColor } from '../../utils';
import { LEAD_FILTER_FIELDS } from '../../constants/filters';
import {
  Users,
  TrendingUp,
  Filter,
  Target,
  UserCheck,
  Download,
  PlusCircle,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { LeadData } from '../../components/leads/CreateLeadForm';

interface FilterField {
  id: string;
  label: string;
  options: { value: string; label: string }[];
}

type Lead = {
  id: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Proposal' | 'Converted' | 'Lost';
  assignedTo: string;
  createdDate: string;
  value: string;
};

const Leads = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const filterFields: FilterField[] = LEAD_FILTER_FIELDS;
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const [showCreateLeadDrawer, setShowCreateLeadDrawer] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([
    {
      id: 'LD001',
      name: 'John Smith',
      email: 'john.smith@email.com',
      phone: '+1 234-567-8900',
      source: 'Website',
      status: 'New',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-15',
      value: '$10,000',
    },
    {
      id: 'LD002',
      name: 'Emily Davis',
      email: 'emily.davis@email.com',
      phone: '+1 234-567-8901',
      source: 'Referral',
      status: 'Contacted',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-14',
      value: '$25,000',
    },
    {
      id: 'LD003',
      name: 'Robert Brown',
      email: 'robert.brown@email.com',
      phone: '+1 234-567-8902',
      source: 'Social Media',
      status: 'Qualified',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-13',
      value: '$50,000',
    },
    {
      id: 'LD004',
      name: 'Lisa Anderson',
      email: 'lisa.anderson@email.com',
      phone: '+1 234-567-8903',
      source: 'Email Campaign',
      status: 'Proposal',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-12',
      value: '$15,000',
    },
    {
      id: 'LD005',
      name: 'Michael Chen',
      email: 'michael.chen@email.com',
      phone: '+1 234-567-8904',
      source: 'Cold Call',
      status: 'New',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-11',
      value: '$35,000',
    },
    {
      id: 'LD006',
      name: 'Jennifer White',
      email: 'jennifer.white@email.com',
      phone: '+1 234-567-8905',
      source: 'Website',
      status: 'Contacted',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-10',
      value: '$20,000',
    },
    {
      id: 'LD007',
      name: 'David Martinez',
      email: 'david.martinez@email.com',
      phone: '+1 234-567-8906',
      source: 'Referral',
      status: 'Qualified',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-09',
      value: '$45,000',
    },
    {
      id: 'LD008',
      name: 'Amanda Taylor',
      email: 'amanda.taylor@email.com',
      phone: '+1 234-567-8907',
      source: 'Social Media',
      status: 'Proposal',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-08',
      value: '$30,000',
    },
    {
      id: 'LD009',
      name: 'Christopher Lee',
      email: 'chris.lee@email.com',
      phone: '+1 234-567-8908',
      source: 'Email Campaign',
      status: 'Converted',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-07',
      value: '$60,000',
    },
    {
      id: 'LD010',
      name: 'Jessica Brown',
      email: 'jessica.brown@email.com',
      phone: '+1 234-567-8909',
      source: 'Website',
      status: 'Lost',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-06',
      value: '$12,000',
    },
    {
      id: 'LD011',
      name: 'Daniel Wilson',
      email: 'daniel.wilson@email.com',
      phone: '+1 234-567-8910',
      source: 'Cold Call',
      status: 'New',
      assignedTo: 'Sarah Johnson',
      createdDate: '2024-01-05',
      value: '$28,000',
    },
    {
      id: 'LD012',
      name: 'Michelle Garcia',
      email: 'michelle.garcia@email.com',
      phone: '+1 234-567-8911',
      source: 'Referral',
      status: 'Contacted',
      assignedTo: 'Mike Wilson',
      createdDate: '2024-01-04',
      value: '$40,000',
    },
  ]);

  const handleCreateLead = () => {
    setShowCreateLeadDrawer(true);
  };

  const handleLeadSubmit = async (data: LeadData) => {
    setIsCreatingLead(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const newLead: Lead = {
        id: `LD${(leads.length + 1).toString().padStart(3, '0')}`,
        name: data.name,
        email: data.email,
        phone: data.phone,
        source: data.source,
        status: data.status,
        assignedTo: data.assignedTo,
        createdDate: new Date().toISOString().split('T')[0],
        value: data.value > 0 ? `$${data.value.toLocaleString()}` : '$0',
      };

      setLeads(prev => [newLead, ...prev]);

      setShowCreateLeadDrawer(false);
      setIsCreatingLead(false);

      alert('Lead created successfully!');
    } catch (error) {
      console.error('Error creating lead:', error);
      setIsCreatingLead(false);
      alert('Error creating lead. Please try again.');
    }
  };

  const columns: DataTableColumn<Lead>[] = [
    {
      id: 'id',
      header: 'Lead ID',
      accessor: (row: Lead) => row.id,
      sortable: true,
      sortValue: (row: Lead) => row.id,
      width: '110px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: (row: Lead) => row.name,
      sortable: true,
      sortValue: (row: Lead) => row.name,
      width: '140px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: (row: Lead) => row.email,
      sortable: true,
      sortValue: (row: Lead) => row.email,
      width: '200px',
    },
    {
      id: 'phone',
      header: 'Phone',
      accessor: (row: Lead) => row.phone,
      sortable: true,
      sortValue: (row: Lead) => row.phone,
      width: '160px',
    },
    {
      id: 'source',
      header: 'Source',
      accessor: (row: Lead) => row.source,
      sortable: true,
      sortValue: (row: Lead) => row.source,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: Lead) => (
        <Badge
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
            row.status,
            'lead'
          )}`}
        >
          {row.status}
        </Badge>
      ),
      sortable: true,
      sortValue: (row: Lead) => row.status,
      width: '130px',
    },
    {
      id: 'assignedTo',
      header: 'Assigned To',
      accessor: (row: Lead) => row.assignedTo,
      sortable: true,
      sortValue: (row: Lead) => row.assignedTo,
      width: '150px',
    },
    {
      id: 'value',
      header: 'Value',
      accessor: (row: Lead) => row.value,
      sortable: true,
      sortValue: (row: Lead) => row.value,
      width: '120px',
    },
    {
      id: 'createdDate',
      header: 'Created',
      accessor: (row: Lead) => row.createdDate,
      sortable: true,
      sortValue: (row: Lead) => row.createdDate,
      width: '140px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: (row: Lead) => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View lead:', row.id)}
          onEdit={() => console.log('Edit lead:', row.id)}
          onDelete={() => console.log('Delete lead:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchReset = () => {
    setSearchTerm('');
  };

  const handleFiltersApply = (filters: { [key: string]: string }) => {
    setActiveFilters(filters);
  };

  const handleFiltersClear = () => {
    setActiveFilters({});
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch =
      searchTerm === '' ||
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      lead.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      !activeFilters.status ||
      lead.status.toLowerCase() === activeFilters.status.toLowerCase();

    const matchesSource =
      !activeFilters.source ||
      lead.source.toLowerCase().includes(activeFilters.source.toLowerCase());

    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleAddNewLead = () => {
    handleCreateLead();
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Leads"
          description="Manage and track your sales leads"
          actionLabel="Add Lead"
          actionIcon={PlusCircle}
          onAction={handleAddNewLead}
        />
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <StatsCard
          title="Total Leads"
          value="1,234"
          change="+12% from last month"
          icon={Users}
        />

        <StatsCard
          title="New Leads"
          value="89"
          change="+8% from last week"
          icon={TrendingUp}
        />

        <StatsCard
          title="Qualified"
          value="156"
          change="+23% from last month"
          icon={UserCheck}
        />

        <StatsCard
          title="Conversion Rate"
          value="24%"
          change="+2% from last month"
          icon={Target}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>All Leads</CardTitle>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInlineFilters(!showInlineFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineFilter
            showFilters={showInlineFilters}
            onToggle={() => setShowInlineFilters(!showInlineFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={handleFiltersClear}
            onFiltersApply={handleFiltersApply}
            title="Filter Leads"
          />
          <DataTable
            columns={columns}
            data={filteredLeads}
            searchValue={searchTerm}
            searchPlaceholder="Search by name, email, source, assigned to, value..."
            onSearchChange={handleSearchChange}
            onSearchReset={handleSearchReset}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
            initialSortBy={{ id: 'createdDate', desc: true }}
          />
        </CardContent>
      </Card>

      <DrawerForm<LeadData>
        open={showCreateLeadDrawer}
        onOpenChange={setShowCreateLeadDrawer}
        title="Create New Lead"
        description="Add a new lead to your sales pipeline"
        onSubmit={handleLeadSubmit}
        submitText="Create Lead"
        loading={isCreatingLead}
        width="lg"
      >
        <CreateLeadForm onSubmit={handleLeadSubmit} />
      </DrawerForm>
    </div>
  );
};

export default Leads;
